import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Alert, Chip, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Skeleton, TextField, Tabs, Tab, Tooltip, Snackbar,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MailIcon from '@mui/icons-material/Mail';
import Layout from '../components/Layout';
import {
  fetchGraphStatus, triggerGraphPoll, fetchVerdicts, overrideVerdict,
  GraphStatus, SenderVerdict, apiErrorText } from '../api/client';

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; }
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return Math.round(n * 100) + '%';
}

type FilterTab = 'all' | 'supplier' | 'non_supplier';

export default function LieferantenTriagePage() {
  const [verdicts, setVerdicts] = useState<SenderVerdict[]>([]);
  const [status, setStatus] = useState<GraphStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [tab, setTab] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [vs, st] = await Promise.all([fetchVerdicts(), fetchGraphStatus()]);
      setVerdicts(vs);
      setStatus(st);
    } catch (e: any) {
      setError(apiErrorText(e) || 'Fehler beim Laden der Lieferanten-Prüfung');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handlePoll = async () => {
    setPolling(true);
    setError(null);
    try {
      await triggerGraphPoll();
      setToast('Postfach wird geprüft — neue Rechnungen erscheinen in Kürze.');
      // Give the background poll a moment, then reload the learned verdicts.
      setTimeout(() => { refresh(); }, 8000);
    } catch (e: any) {
      setError(apiErrorText(e) || 'Postfach-Prüfung konnte nicht gestartet werden');
    } finally {
      setPolling(false);
    }
  };

  const handleToggle = async (row: SenderVerdict, next: boolean) => {
    setSavingId(row.id);
    // Optimistic update.
    setVerdicts(vs => vs.map(v =>
      v.id === row.id ? { ...v, is_supplier: next, source: 'manual' } : v));
    try {
      await overrideVerdict(row.id, next);
      setToast(`„${row.display_name || row.key_value}“ als ${next ? 'Lieferant' : 'kein Lieferant'} gespeichert.`);
    } catch (e: any) {
      // Revert on failure.
      setVerdicts(vs => vs.map(v =>
        v.id === row.id ? { ...v, is_supplier: row.is_supplier, source: row.source } : v));
      setError(apiErrorText(e) || 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return verdicts.filter(v => {
      if (tab === 'supplier' && !v.is_supplier) return false;
      if (tab === 'non_supplier' && v.is_supplier) return false;
      if (q) {
        const hay = `${v.display_name || ''} ${v.key_value} ${v.category || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [verdicts, tab, search]);

  const supplierCount = verdicts.filter(v => v.is_supplier).length;

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Lieferanten-Prüfung
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Die KI lernt aus dem Rechnungs-Postfach, welche Absender echte Waren­lieferanten
          sind (deren Preise getrackt werden). Hier kannst du jede Entscheidung prüfen und
          per Schalter korrigieren. Korrekturen überschreibt die KI nie.
        </Typography>
      </Box>

      {/* Mailbox status + poll */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <MailIcon color={status?.configured ? 'success' : 'disabled'} />
            <Box sx={{ flex: 1 }}>
              {status ? (
                status.configured ? (
                  <Typography variant="body2">
                    Postfach <strong>{status.mailbox}</strong> · automatische Prüfung
                    alle {status.poll_interval_min} Min.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Postfach noch nicht konfiguriert.
                  </Typography>
                )
              ) : (
                <Skeleton width={280} />
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<MailIcon />}
              onClick={handlePoll}
              disabled={polling || !status?.configured}
            >
              {polling ? 'Wird geprüft…' : 'Jetzt prüfen'}
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
              Aktualisieren
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="all" label={`Alle (${verdicts.length})`} />
          <Tab value="supplier" label={`Lieferant (${supplierCount})`} />
          <Tab value="non_supplier" label={`Kein Lieferant (${verdicts.length - supplierCount})`} />
        </Tabs>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          label="Suchen"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, Domain, Kategorie…"
        />
      </Stack>

      {loading ? (
        <Skeleton variant="rounded" height={240} />
      ) : filtered.length === 0 ? (
        <Alert severity="info">
          {verdicts.length === 0
            ? 'Noch keine gelernten Verdicts. Starte eine Postfach-Prüfung.'
            : 'Keine Einträge für diese Filter.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Aussteller / Domain</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Kategorie</TableCell>
                <TableCell align="center">Lieferant?</TableCell>
                <TableCell align="right">KI-Sicherheit</TableCell>
                <TableCell>Quelle</TableCell>
                <TableCell align="right">Gesehen</TableCell>
                <TableCell>Zuletzt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {v.display_name || v.key_value}
                    </Typography>
                    {v.reason && (
                      <Typography variant="caption" color="text.secondary">
                        {v.reason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={v.kind === 'domain' ? 'Domain' : 'Aussteller'}
                    />
                  </TableCell>
                  <TableCell>{v.category || '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={v.is_supplier ? 'Preise werden getrackt' : 'Wird ignoriert'}>
                      <Switch
                        checked={v.is_supplier}
                        disabled={savingId === v.id}
                        onChange={(e) => handleToggle(v, e.target.checked)}
                        color="success"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{fmtPct(v.confidence)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={v.source === 'manual' ? 'primary' : 'default'}
                      label={v.source === 'manual' ? 'Manuell' : 'KI'}
                    />
                  </TableCell>
                  <TableCell align="right">{v.times_seen}</TableCell>
                  <TableCell>{fmtDate(v.last_seen)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Layout>
  );
}
